/**
 * Haus J façade: hotspots (data-house-map-tooltip) + shared #house-map-tooltip (hover).
 * SVG geometry is static; #house-map-items-data supplies permalink + aria-label per plan id.
 */
import { initAnchorTooltips } from "./anchor-tooltips.js";

/** Match `_variables.scss` / `respond-min($screen-sm)` so tooltips behave like CSS breakpoints. */
const HOUSE_MAP_TOOLTIP_VIEWPORT_MIN_PX = 769;

/** Narrow viewports / touch-first: no hover panel; links remain tappable (see `_house-map.scss`). */
function houseMapAllowsHoverTooltip() {
  return window.matchMedia(
    `(hover: hover) and (min-width: ${HOUSE_MAP_TOOLTIP_VIEWPORT_MIN_PX}px)`,
  ).matches;
}

/** @typedef {{ available?: boolean, permalink?: string, ariaLabel?: string }} HouseMapLinkRow */

/** @returns {Record<string, HouseMapLinkRow> | null} */
function parseHouseMapLinkData() {
  const el = document.getElementById("house-map-items-data");
  if (!el?.textContent?.trim()) {
    return null;
  }
  try {
    const parsed = JSON.parse(el.textContent);
    return typeof parsed === "object" && parsed ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * @param {Record<string, HouseMapLinkRow>} data
 * @param {string} rawSuffix
 */
function resolveHouseMapRow(data, rawSuffix) {
  const row = data[rawSuffix];
  if (row) {
    return row;
  }
  const lk = rawSuffix.toLowerCase();
  const key = Object.keys(data).find((k) => k.toLowerCase() === lk);
  return key ? data[key] : null;
}

/**
 * Hydrate static Illustrator SVG anchors from WordPress (apartments template only).
 * @param {string} wrapperSelector
 */
function hydrateHouseMapLinks(wrapperSelector) {
  const data = parseHouseMapLinkData();
  if (!data) {
    return;
  }

  const wrap =
    typeof wrapperSelector === "string"
      ? document.querySelector(wrapperSelector)
      : null;
  if (!wrap) {
    return;
  }

  wrap
    .querySelectorAll("[data-house-map-tooltip] .house-map__hit")
    .forEach((anchor) => {
      const marker = anchor.closest("[data-house-map-tooltip]");
      const raw =
        marker && marker.getAttribute("data-house-map-tooltip")
          ? marker.getAttribute("data-house-map-tooltip").trim()
          : "";
      if (!raw) {
        return;
      }
      const row = resolveHouseMapRow(data, raw);
      if (!row || typeof row !== "object") {
        anchor.removeAttribute("href");
        anchor.setAttribute("tabindex", "-1");
        anchor.setAttribute("aria-disabled", "true");
        return;
      }
      const url = typeof row.permalink === "string" ? row.permalink.trim() : "";
      const isAvailable = row.available === true && url !== "";
      const label =
        typeof row.ariaLabel === "string" ? row.ariaLabel.trim() : "";
      if (label) {
        anchor.setAttribute("aria-label", label);
      }

      if (!isAvailable) {
        anchor.removeAttribute("href");
        anchor.setAttribute("tabindex", "-1");
        anchor.setAttribute("aria-disabled", "true");
        return;
      }

      anchor.setAttribute("href", url);
      anchor.removeAttribute("tabindex");
      anchor.removeAttribute("aria-disabled");
    });
}

function boot() {
  hydrateHouseMapLinks(".house-map__wrapper");
  initAnchorTooltips(".house-map__wrapper", {
    tooltipId: "house-map-tooltip",
    templatePrefix: "house-map-tooltip-",
    markerSelector: "[data-house-map-tooltip]",
    markerDataAttr: "data-house-map-tooltip",
    canShowHoverTooltip: houseMapAllowsHoverTooltip,
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
